import { useState, useEffect, FormEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, FileText, DollarSign, Users, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Tabs } from '@/components/Tabs';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useSites } from '@/hooks/useSites';
import { useClients } from '@/hooks/useClients';
import { TripTypeSelector, RateUnitSelector, getDefaultRateUnitForTripType } from '@/components/contracts/TripTypeSelector';
import { InvestorRatesEditor } from '@/components/contracts/InvestorRatesEditor';
import { LiveMathPreview } from '@/components/contracts/LiveMathPreview';
import { ScenariosTable } from '@/components/contracts/ScenariosTable';
import type {
  SiteContract, SiteContractInput, ContractStatus, TripType, RateUnit, InvestorRateInput,
} from '@/lib/types';
import { InvestorRatesPanel } from '@/components/contracts/InvestorRatesPanel';

interface ContractFormProps {
  initial?: SiteContract | null;
  onSubmit: (input: SiteContractInput) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  /** Pre-fill site (when creating contract from a site detail page) */
  defaultSiteId?: string;
}

const STATUSES: ContractStatus[] = ['draft', 'active', 'expired', 'terminated', 'on_hold'];

const blank: SiteContractInput = {
  siteId: '', clientId: '',
  tripType: 'water', rateUnit: 'per_m3',
  targetQty: 28,
  clientRateAtTarget: 0,
  driverRateAtTarget: 0,
  effectiveFrom: new Date().toISOString().slice(0, 10),
  effectiveTo: null,
  contractNumber: '',
  signedContractUrl: '',
  status: 'active',
  notes: '',
  investorRates: [],
};

export function ContractForm({ initial, onSubmit, onCancel, submitLabel, defaultSiteId }: ContractFormProps) {
  const { t } = useTranslation();
  const { data: sitesData } = useSites({ pageSize: 100, status: 'active' });
  const { data: clientsData } = useClients({ pageSize: 100, status: 'active' });
  const sites = sitesData?.items ?? [];
  const clients = clientsData?.items ?? [];

  const [form, setForm] = useState<SiteContractInput>(blank);
  const [previewQty, setPreviewQty] = useState<number>(28);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PR-CONTRACT-WATER MAY 11 — water station picker state. Loaded from
  // /api/v1/water-stations and persisted via PATCH defaultWaterStationId.
  interface WaterStationOpt {
    id: string;
    code: string;
    name_en: string;
    supplier_name: string | null;
    serves_site_id: string | null;
    rate_per_m3_sar: string;
    status: string;
  }
  const [waterStations, setWaterStations] = useState<WaterStationOpt[]>([]);
  const [waterStationId, setWaterStationId] = useState<string>('');
  const [savingStation, setSavingStation] = useState(false);
  const [stationErr, setStationErr] = useState<string | null>(null);
  useEffect(() => {
    apiClient.get<{ items: WaterStationOpt[] }>('/api/v1/water-stations?status=active')
      .then((r) => setWaterStations(r.data.items ?? []))
      .catch(() => setWaterStations([]));
  }, []);
  useEffect(() => {
    const init = initial as (typeof initial & { defaultWaterStationId?: string | null });
    setWaterStationId(init?.defaultWaterStationId ?? '');
  }, [initial]);
  const selectedStation = waterStations.find((s) => s.id === waterStationId);
  const waterRatePerM3 = selectedStation ? Number(selectedStation.rate_per_m3_sar) : null;
  const waterStationLabel = selectedStation ? `${selectedStation.code} — ${selectedStation.name_en}` : null;
  async function persistStation(newId: string) {
    if (!initial?.id) {
      setWaterStationId(newId);
      return;
    }
    setSavingStation(true); setStationErr(null);
    try {
      await apiClient.patch('/api/v1/site-contracts/' + initial.id, {
        defaultWaterStationId: newId || null,
      });
      setWaterStationId(newId);
    } catch (e) { setStationErr(extractErrorMessage(e)); }
    finally { setSavingStation(false); }
  }

  useEffect(() => {
    if (initial) {
      setForm({
        siteId: initial.siteId,
        clientId: initial.clientId,
        tripType: initial.tripType,
        rateUnit: initial.rateUnit,
        targetQty: Number(initial.targetQty),
        clientRateAtTarget: Number(initial.clientRateAtTarget),
        driverRateAtTarget: Number(initial.driverRateAtTarget),
        effectiveFrom: initial.effectiveFrom.slice(0, 10),
        effectiveTo: initial.effectiveTo ? initial.effectiveTo.slice(0, 10) : null,
        contractNumber: initial.contractNumber ?? '',
        signedContractUrl: initial.signedContractUrl ?? '',
        status: initial.status,
        notes: initial.notes ?? '',
        investorRates: initial.investorRates.map((r) => ({
          investorId: r.investorId,
          investorRateAtTarget: Number(r.investorRateAtTarget),
          notes: r.notes,
        })),
      });
      setPreviewQty(Number(initial.targetQty));
    } else {
      setForm({ ...blank, siteId: defaultSiteId ?? '' });
      setPreviewQty(blank.targetQty);
    }
  }, [initial, defaultSiteId]);

  const update = <K extends keyof SiteContractInput>(key: K, value: SiteContractInput[K]) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Auto-update rate_unit when trip_type changes
      if (key === 'tripType') {
        next.rateUnit = getDefaultRateUnitForTripType(value as TripType);
      }
      return next;
    });
  };

  // First investor's rate (used for preview)
  const firstInvestorRate = form.investorRates?.[0]?.investorRateAtTarget ?? 0;

  const totalDistributed = useMemo(() => {
    return form.investorRates?.length ?? 0;
  }, [form.investorRates]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.siteId) { setError(t('contracts.errors.siteRequired')); return; }
    if (!form.clientId) { setError(t('contracts.errors.clientRequired')); return; }
    if (form.targetQty <= 0) { setError(t('contracts.errors.targetPositive')); return; }
    if (form.clientRateAtTarget <= 0) { setError(t('contracts.errors.clientRatePositive')); return; }
    if (form.driverRateAtTarget < 0) { setError(t('contracts.errors.driverRateNonneg')); return; }
    if (!form.effectiveFrom) { setError(t('contracts.errors.effectiveFromRequired')); return; }
    if (form.effectiveTo && form.effectiveTo < form.effectiveFrom) {
      setError(t('contracts.errors.dateOrder')); return;
    }
    for (const ir of form.investorRates ?? []) {
      if (ir.investorRateAtTarget > form.clientRateAtTarget) {
        setError(t('contracts.errors.investorExceedsClient')); return;
      }
    }

    const payload: SiteContractInput = {
      siteId: form.siteId,
      clientId: form.clientId,
      tripType: form.tripType,
      rateUnit: form.rateUnit,
      targetQty: Number(form.targetQty),
      clientRateAtTarget: Number(form.clientRateAtTarget),
      driverRateAtTarget: Number(form.driverRateAtTarget),
      effectiveFrom: form.effectiveFrom,
      status: form.status,
    };
    if (form.effectiveTo) payload.effectiveTo = form.effectiveTo;
    if (form.contractNumber?.trim()) payload.contractNumber = form.contractNumber.trim();
    if (form.signedContractUrl?.trim()) payload.signedContractUrl = form.signedContractUrl.trim();
    if (form.notes?.trim()) payload.notes = form.notes.trim();
    if (form.investorRates && form.investorRates.length > 0) {
      payload.investorRates = form.investorRates;
    }

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const BasicTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('contracts.section.parties')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('contracts.field.site')} required>
            <Select value={form.siteId ?? ''} onChange={(v) => update('siteId', v)}
              placeholder={t('contracts.placeholder.selectSite')}>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.nameEn}</option>)}
            </Select>
          </Field>
          <Field label={t('contracts.field.client')} required>
            <Select value={form.clientId ?? ''} onChange={(v) => update('clientId', v)}
              placeholder={t('contracts.placeholder.selectClient')}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nameEn}</option>)}
            </Select>
          </Field>
        </div>
      </fieldset>
      <TripTypeSelector value={form.tripType} onChange={(v) => update('tripType', v)} />
      <RateUnitSelector value={form.rateUnit ?? 'per_m3'} onChange={(v) => update('rateUnit', v as RateUnit)} />

      {/* PR-CONTRACT-WATER MAY 11 — water filling station picker, water trips only */}
      {form.tripType === 'water' && (
        <fieldset className="space-y-3 pt-3 border-t border-border">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Water filling station (optional)
          </legend>
          <Field label="Station">
            <select
              value={waterStationId}
              onChange={(e) => persistStation(e.target.value)}
              disabled={savingStation}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base"
            >
              <option value="">— None (water cost not modeled) —</option>
              {(() => {
                const matchSite = waterStations.filter((s) => s.serves_site_id === form.siteId);
                const others    = waterStations.filter((s) => s.serves_site_id !== form.siteId);
                return [...matchSite, ...others].map((s) => {
                  const rec = s.serves_site_id === form.siteId ? ' · recommended' : '';
                  return (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.name_en} — {Number(s.rate_per_m3_sar).toFixed(3)} SAR/m³{rec}
                    </option>
                  );
                });
              })()}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Trips on this contract auto-inherit this station. Stations serving this site are highlighted as <em>recommended</em>.
              {savingStation && <span className="ms-2"><Loader2 className="h-3 w-3 inline animate-spin" /> saving…</span>}
            </p>
            {stationErr && <p className="text-[11px] text-rose-700 dark:text-rose-400 mt-1">{stationErr}</p>}
          </Field>
        </fieldset>
      )}

      <fieldset className="space-y-3 pt-3 border-t border-border">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('contracts.section.dates')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('contracts.field.effectiveFrom')} required>
            <Input type="date" value={form.effectiveFrom ?? ''} onChange={(e) => update('effectiveFrom', e.target.value)} />
          </Field>
          <Field label={t('contracts.field.effectiveTo')}>
            <Input type="date" value={form.effectiveTo ?? ''} onChange={(e) => update('effectiveTo', e.target.value || null)} />
            <p className="text-[11px] text-muted-foreground mt-1">{t('contracts.help.openEnded')}</p>
          </Field>
        </div>
      </fieldset>

      <fieldset className="space-y-3 pt-3 border-t border-border">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('contracts.section.documents')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('contracts.field.contractNumber')}>
            <Input value={form.contractNumber ?? ''} onChange={(e) => update('contractNumber', e.target.value)}
              placeholder="AMAALA-WATER-001" className="font-mono" />
          </Field>
          <Field label={t('contracts.field.status')}>
            <Select value={form.status ?? 'active'} onChange={(v) => update('status', v as ContractStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{t(`contracts.status.${s}`)}</option>)}
            </Select>
          </Field>
        </div>
        <Field label={t('contracts.field.signedContractUrl')}>
          <Input dir="ltr" value={form.signedContractUrl ?? ''} onChange={(e) => update('signedContractUrl', e.target.value)}
            placeholder="https://..." className="font-mono text-xs" />
        </Field>
        <Field label={t('contracts.field.notes')}>
          <textarea value={form.notes ?? ''} onChange={(e) => update('notes', e.target.value)} rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </Field>
      </fieldset>
    </div>
  );

  const RatesTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="rounded-md bg-blue-500/5 border border-blue-500/20 p-3 text-xs text-foreground">
        {t('contracts.help.ratesIntro')}
      </div>

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('contracts.section.target')}
        </legend>
        <Field label={t('contracts.field.targetQty')} required>
          <div className="flex items-center gap-2">
            <Input type="number" step="0.001" min={0.001} dir="ltr"
              value={form.targetQty} onChange={(e) => update('targetQty', Number(e.target.value) || 0)}
              className="font-mono" placeholder="28" />
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {form.rateUnit === 'per_m3' ? 'm³' : form.rateUnit === 'per_ton' ? 't' : form.rateUnit === 'per_km' ? 'km' : t('contracts.preview.trip')}
            </span>
          </div>
          {form.rateUnit === 'per_m3' && form.tripType === 'water' && (
            <p className="text-[11px] text-muted-foreground mt-1">{t('contracts.help.water28')}</p>
          )}
        </Field>
      </fieldset>

      <fieldset className="space-y-3 pt-3 border-t border-border">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('contracts.section.rates')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('contracts.field.clientRate')} required>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.01" min={0.01} dir="ltr"
                value={form.clientRateAtTarget || ''} onChange={(e) => update('clientRateAtTarget', Number(e.target.value) || 0)}
                className="font-mono" placeholder="100" />
              <span className="text-sm text-muted-foreground whitespace-nowrap">{t('common.sar')}</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{t('contracts.help.clientRate')}</p>
          </Field>
          <Field label={t('contracts.field.driverRate')} required>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.01" min={0} dir="ltr"
                value={form.driverRateAtTarget || ''} onChange={(e) => update('driverRateAtTarget', Number(e.target.value) || 0)}
                className="font-mono" placeholder="20" />
              <span className="text-sm text-muted-foreground whitespace-nowrap">{t('common.sar')}</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{t('contracts.help.driverRate')}</p>
          </Field>
        </div>
      </fieldset>
    </div>
  );

  const InvestorsTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="rounded-md bg-amber-500/5 border border-amber-500/20 p-3 text-xs text-foreground">
        {t('contracts.help.investorRatesIntro')}
      </div>
      <InvestorRatesEditor
        rates={form.investorRates ?? []}
        onChange={(rates: InvestorRateInput[]) => update('investorRates', rates)}
        clientRateAtTarget={form.clientRateAtTarget}
        driverRateAtTarget={form.driverRateAtTarget}
      />
      {/* PR-INV-RATES MAY 11 — time-versioned investor rates panel (only when editing an existing contract) */}
      {initial?.id && (
        <div className="pt-6 border-t border-border">
          <InvestorRatesPanel
            siteContractId={initial.id}
            defaultRateLabel={`${Number(form.clientRateAtTarget).toFixed(2)} SAR · client · ${(form.rateUnit ?? 'per_m3').replace('_', ' ')}`}
          />
        </div>
      )}
    </div>
  );

  const PreviewTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {totalDistributed === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center">
          <p className="text-sm text-muted-foreground">{t('contracts.preview.noInvestors')}</p>
          <p className="text-[11px] text-muted-foreground mt-1">{t('contracts.preview.addInvestorFirst')}</p>
        </div>
      ) : (
        <>
          <fieldset className="space-y-3">
            <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              {t('contracts.preview.testScenario')}
            </legend>
            <Field label={t('contracts.preview.deliveredQty')}>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.1" min={0} max={form.targetQty * 1.5} dir="ltr"
                  value={previewQty} onChange={(e) => setPreviewQty(Number(e.target.value) || 0)}
                  className="font-mono" />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  / {form.targetQty} {form.rateUnit === 'per_m3' ? 'm³' : form.rateUnit === 'per_ton' ? 't' : form.rateUnit === 'per_km' ? 'km' : t('contracts.preview.trip')}
                </span>
              </div>
              <input type="range" min={0} max={form.targetQty} step={form.targetQty / 100}
                value={previewQty} onChange={(e) => setPreviewQty(Number(e.target.value))}
                className="w-full mt-2" />
            </Field>
          </fieldset>

          <LiveMathPreview
            deliveredQty={previewQty}
            targetQty={form.targetQty}
            clientRateAtTarget={form.clientRateAtTarget}
            investorRateAtTarget={firstInvestorRate}
            driverRateAtTarget={form.driverRateAtTarget}
            rateUnit={form.rateUnit}
            waterRatePerM3={waterRatePerM3}
            waterStationLabel={waterStationLabel}
          />

          {(form.investorRates?.length ?? 0) > 1 && (
            <p className="text-[11px] text-muted-foreground text-center">
              {t('contracts.preview.firstInvestorOnly', { name: form.investorRates![0].investorId.slice(0, 8) })}
            </p>
          )}

          <ScenariosTable
            targetQty={form.targetQty}
            clientRateAtTarget={form.clientRateAtTarget}
            investorRateAtTarget={firstInvestorRate}
            driverRateAtTarget={form.driverRateAtTarget}
            rateUnit={form.rateUnit}
          />
        </>
      )}
    </div>
  );

  const tabs = [
    { key: 'basic',     label: <span className="inline-flex items-center gap-1.5"><FileText className="h-4 w-4" />{t('contracts.tab.basic')}</span>, content: BasicTab },
    { key: 'rates',     label: <span className="inline-flex items-center gap-1.5"><DollarSign className="h-4 w-4" />{t('contracts.tab.rates')}</span>, content: RatesTab },
    { key: 'investors', label: <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" />{t('contracts.tab.investors')}</span>, content: InvestorsTab },
    { key: 'preview',   label: <span className="inline-flex items-center gap-1.5"><Calculator className="h-4 w-4" />{t('contracts.tab.preview')}</span>, content: PreviewTab },
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <Tabs tabs={tabs} defaultKey="basic" />
      <div className="shrink-0 border-t border-border bg-muted/30">
        {error && (
          <p className="text-sm text-destructive p-3 mx-6 my-3 rounded bg-destructive/10 border border-destructive/30 flex items-start gap-2" role="alert">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
          </p>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-6">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>{t('common.cancel')}</Button>
          )}
          <Button type="submit" variant="gold" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel ?? t('common.save')}
          </Button>
        </div>
      </div>
    </form>
  );
}

function Field({ label, required, className, children }: { label: string; required?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
      {children}
    </div>
  );
}

function Select({ value, onChange, children, placeholder }: { value: string; onChange: (v: string) => void; children: React.ReactNode; placeholder?: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {children}
    </select>
  );
}
